// The results page as the results PDF prints it (Edward, Oct 24: the PDF is "an identical copy
// of the results page"). The API opens this in headless Chromium (apps/api/src/email/page-pdf.ts)
// and hands it the result in window.__GP_PRINT__, so the page asks the API for nothing: no
// sign-in, no reading, no tracking. Light theme always; it marks <html data-print-ready> once
// the fonts are in, and the server prints then.
import { useEffect } from "react";
import { Message } from "../components/Page";
import { FacetMark, Wordmark } from "../components/Wordmark";
import { ResultBodyV4 } from "../components/ResultV4";
import { isV4Result, type ResultView, type ResultViewV4 } from "../lib/types";

export interface PrintData {
  result: ResultView | ResultViewV4;
  /** Results emails go out, so copy may point people to them. */
  emailEnabled: boolean;
  /** Which take this is ("Belief Architecture · Oct 29, 2026"), beside the logo on the first page. */
  stamp?: string;
}

declare global {
  interface Window {
    __GP_PRINT__?: PrintData;
  }
}

export function ResultPrint() {
  const data = window.__GP_PRINT__;
  const printable = !!data && isV4Result(data.result);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", "light");
    let live = true;
    const fonts = document.fonts?.ready ?? Promise.resolve();
    void fonts.then(() =>
      requestAnimationFrame(() => {
        if (live) root.setAttribute("data-print-ready", printable ? "ok" : "missing");
      }),
    );
    return () => {
      live = false;
    };
  }, [printable]);

  if (!data || !isV4Result(data.result)) return <Message title="Nothing to print" />;
  return (
    <div className="mx-auto flex w-full max-w-[720px] flex-col px-5 pb-10">
      <PrintHeader firstName={data.result.firstName} stamp={data.stamp} />
      <main className="flex flex-1 flex-col">
        <ResultBodyV4 result={data.result} emailEnabled={data.emailEnabled} print />
      </main>
    </div>
  );
}

/**
 * The top of the PDF's first page, in the homepage's colours (Edward, Oct 26): the four-colour
 * stripe, the logo, whose profile it is, and which take (event and date).
 */
function PrintHeader({ firstName, stamp }: { firstName: string; stamp?: string }) {
  const stripe = ["var(--color-sun)", "var(--color-coral)", "var(--color-leaf)", "var(--color-teal)"];
  return (
    <header className="relative mt-6 overflow-hidden rounded-[18px] bg-soft" data-testid="print-header">
      <div aria-hidden="true" className="flex h-2">
        {stripe.map((c) => (
          <span key={c} className="flex-1" style={{ backgroundColor: c }} />
        ))}
      </div>
      <FacetMark size={150} className="pointer-events-none absolute -right-8 top-[-30px] opacity-[0.08]" />
      <div className="relative flex items-center justify-between gap-6 px-6 py-5">
        <Wordmark link={false} size={30} />
        <div className="min-w-0 text-right">
          <p className="text-[15px] font-bold">{firstName ? `${firstName}'s Gifts Profile` : "Your Gifts Profile"}</p>
          {stamp ? (
            <p className="mt-0.5 text-[13px] text-muted" data-testid="print-stamp">
              {stamp}
            </p>
          ) : null}
        </div>
      </div>
    </header>
  );
}
